import React from "react";
import { Typography } from "@mui/material";
import DocumentUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LICENSE_STATUSES } from "../utils";
import {
  approvePortOperatorLicenseSchema,
  MAX_PORT_OPERATOR_APPROVAL_NOTES_LENGTH,
} from "./approvePortOperatorLicenseSchema";

interface ApprovePortOperatorLicenseDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  onSubmit?: (payload: { startDate?: string; expiryDate?: string; approvalNotes?: string }) => void;
  refOrderNo: number;
  tradingLicenseValidityDate?: string;
  vendorLicenseExpiryDate?: string;
  licenseStatus?: string;
  showAccessPeriodFields?: boolean;
  onDocumentUpload?: (file: File) => void;
  isUploading?: boolean;
  isSubmitting?: boolean;
}

const EMPTY: UploadEntry[] = [];

type ApprovePortOperatorFormData = {
  startDate?: string;
  expiryDate?: string;
  approvalNotes?: string;
};

const F = {
  START_DATE: "startDate",
  EXPIRY_DATE: "expiryDate",
  APPROVAL_NOTES: "approvalNotes",
} as const;

const toISODate = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getTodayISODate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return toISODate(today);
};

const ApprovePortOperatorLicenseDrawer: React.FC<ApprovePortOperatorLicenseDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  refOrderNo,
  tradingLicenseValidityDate,
  vendorLicenseExpiryDate,
  licenseStatus,
  showAccessPeriodFields,
  onDocumentUpload,
  isUploading,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const effectiveVendorLicenseExpiryDate = vendorLicenseExpiryDate ?? tradingLicenseValidityDate;
  const shouldShowAccessPeriodFields =
    showAccessPeriodFields ??
    licenseStatus === LICENSE_STATUSES.AWAITING_MANAGING_DIRECTOR_APPROVAL;
  const defaultStartDate = shouldShowAccessPeriodFields ? getTodayISODate() : "";

  const schema = React.useMemo(
    () =>
      approvePortOperatorLicenseSchema(
        t,
        effectiveVendorLicenseExpiryDate,
        shouldShowAccessPeriodFields,
      ),
    [t, effectiveVendorLicenseExpiryDate, shouldShowAccessPeriodFields],
  );

  const form = useForm<ApprovePortOperatorFormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      startDate: defaultStartDate,
      expiryDate: "",
      approvalNotes: "",
    },
  });

  const { handleSubmit, reset } = form;

  const approvalFiles = useUploadStore((s) => s.filesByKey.licenseApprovalAttachment ?? EMPTY);
  const isApprovalUploadInFlight = useUploadStore((s) =>
    s.isUploadInFlight("licenseApprovalAttachment"),
  );
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const fileProfiles = useFileUploadProfiles();
  const approvalProfile = fileProfiles.sideDrawerApprovalFile;

  const allowedExtensions = approvalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const resetForm = React.useCallback(() => {
    reset({
      startDate: defaultStartDate,
      expiryDate: "",
      approvalNotes: "",
    });
    clearFilesForKey("licenseApprovalAttachment");
  }, [clearFilesForKey, defaultStartDate, reset]);

  const handleDateFieldChange = (
    key: typeof F.START_DATE | typeof F.EXPIRY_DATE,
    value: unknown,
  ) => {
    const v = value as unknown;
    if (v instanceof Date && !Number.isNaN(v.getTime())) {
      form.setValue(key, toISODate(v), { shouldValidate: true, shouldDirty: true });
    } else {
      form.setValue(key, "", { shouldValidate: true, shouldDirty: true });
    }
  };

  const prevOpenRef = React.useRef(isOpen);
  React.useEffect(() => {
    if (prevOpenRef.current && !isOpen) {
      resetForm();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, resetForm]);

  const handleClose = () => {
    onClose("approveDrawer");
    resetForm();
  };

  const handleCancel = () => {
    onClose("approveDrawer");
    resetForm();
  };

  const handleUpload = (newFiles: File[]) => {
    if (newFiles.length > 1) return;
    const file = newFiles[0];
    if (!file) return;

    setFilesForKey("licenseApprovalAttachment", [file]);
    onDocumentUpload?.(file);
  };

  const handleSubmitForm = (data: ApprovePortOperatorFormData) => {
    const trimmedNotes = data.approvalNotes?.trim() ? data.approvalNotes.trim() : undefined;

    onSubmit?.({
      ...(shouldShowAccessPeriodFields
        ? {
            startDate: data.startDate,
            expiryDate: data.expiryDate,
          }
        : {}),
      approvalNotes: trimmedNotes,
    });
  };

  const isAttachmentUploading = Boolean(isUploading || isApprovalUploadInFlight);

  const handleDrawerSubmit = () => {
    if (isAttachmentUploading) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    void handleSubmit(handleSubmitForm)();
  };

  if (!isOpen) return null;

  return (
    <div data-testid="approve-port-operator-license-drawer-form" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={t("licensing.port-operator-license-approve-title")}
        onClose={handleClose}
        onCancel={handleCancel}
        submitLabel={t("licensing.approve-button")}
        onSubmit={handleDrawerSubmit}
        isSubmitting={Boolean(isSubmitting)}
      >
        <Typography sx={{ px: 3, pb: 2 }} data-testid="approve-port-operator-license-subtitle">
          {t("licensing.port-operator-license-approve-subtitle")}
          <br />
          <span style={{ fontWeight: 600 }}>{refOrderNo}.</span>
        </Typography>

        <div className="flex-1 w-[400px] py-2">
          <div className="flex flex-col gap-2 px-6">
            <FormProvider {...form}>
              {shouldShowAccessPeriodFields && (
                <>
                  <FormItem<ApprovePortOperatorFormData, typeof F.START_DATE>
                    required
                    name={F.START_DATE}
                    type="date-picker"
                    label={t("licensing.access-enabled-from")}
                    timePicker={false}
                    testId="approve-port-operator-start-date"
                    dateConstraint="not-past"
                    onFieldChange={(key, value) => handleDateFieldChange(key, value)}
                  />

                  <FormItem<ApprovePortOperatorFormData, typeof F.EXPIRY_DATE>
                    required
                    name={F.EXPIRY_DATE}
                    type="date-picker"
                    label={t("licensing.access-enabled-to")}
                    timePicker={false}
                    testId="approve-port-operator-expiry-date"
                    dateConstraint="not-past"
                    onFieldChange={(key, value) => handleDateFieldChange(key, value)}
                  />
                </>
              )}

              <FormItem<ApprovePortOperatorFormData, typeof F.APPROVAL_NOTES>
                name={F.APPROVAL_NOTES}
                type="textarea"
                label={t("licensing.notes")}
                testId="approve-port-operator-notes"
                maxChars={MAX_PORT_OPERATOR_APPROVAL_NOTES_LENGTH}
                showCharCounter
                minRows={4}
              />

              <DocumentUpload
                label={t("licensing.attachments")}
                testId="port-operator-license-approve-attachment-upload"
                name="licenseApprovalAttachment"
                maxFiles={1}
                allowedExtensions={allowedExtensions}
                onUpload={handleUpload}
                conditions={approvalProfile}
                files={approvalFiles}
                setFiles={(next) => setFilesForKey("licenseApprovalAttachment", next)}
                uploadDescription={t("upload.dragOrDropDescription")}
              />

              {approvalFiles[0] && (
                <SingleFile
                  file={approvalFiles[0]}
                  fieldName="licenseApprovalAttachment"
                  setFiles={() => clearFilesForKey("licenseApprovalAttachment")}
                  removeFn={() => clearFilesForKey("licenseApprovalAttachment")}
                  isLoading={isAttachmentUploading}
                />
              )}
            </FormProvider>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default ApprovePortOperatorLicenseDrawer;
